import { Link } from '@backstage/core-components';
import {
  AppRootElementBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import LinkIcon from '@material-ui/icons/Link';
import { useTaskEventStream } from '@backstage/plugin-scaffolder-react';
import { useLocation } from 'react-router-dom';

const TASK_PATH = /^\/create\/tasks\/([^/]+)$/;
const PULL_REQUEST_URL =
  /https:\/\/dev\.azure\.com\/[^\s<>"']+\/_git\/[^\s<>"']+\/pullrequest\/\d+/gi;

export function taskIdFromPath(pathname: string): string | undefined {
  return pathname.match(TASK_PATH)?.[1];
}

/** URLs logged by azure:devops:repository:create-pr and wait-pr. */
export function extractPullRequestUrls(
  logs: Record<string, string[]> | undefined,
): string[] {
  const found = new Set<string>();
  for (const lines of Object.values(logs ?? {})) {
    for (const line of lines) {
      for (const match of line.match(PULL_REQUEST_URL) ?? []) {
        found.add(match.replace(/[),.;]+$/, ''));
      }
    }
  }
  return [...found];
}

const useStyles = makeStyles(theme => ({
  card: {
    position: 'fixed',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    zIndex: 1200,
    maxWidth: 440,
    padding: theme.spacing(2),
  },
  link: {
    '&:hover': {
      textDecoration: 'none',
    },
  },
  url: {
    marginTop: theme.spacing(1),
    wordBreak: 'break-all',
  },
}));

const PullRequestTaskBannerForTask = ({ taskId }: { taskId: string }) => {
  const classes = useStyles();
  const taskStream = useTaskEventStream(taskId);
  const urls = extractPullRequestUrls(taskStream.stepLogs);

  if (urls.length === 0 || (taskStream.completed && !taskStream.error)) {
    return null;
  }

  return (
    <Paper className={classes.card} elevation={8} role="status">
      <Typography variant="subtitle1">
        Pull request is ready for review
      </Typography>
      <Typography variant="body2" paragraph>
        This task stays here until the pull request is merged. Open it in Azure
        DevOps to review or approve.
      </Typography>
      {urls.map(url => (
        <div key={url}>
          <Link to={url} className={classes.link}>
            <Button
              startIcon={<LinkIcon />}
              component="div"
              color="primary"
              variant="contained"
            >
              Open pull request
            </Button>
          </Link>
          <Typography variant="caption" component="p" className={classes.url}>
            {url}
          </Typography>
        </div>
      ))}
    </Paper>
  );
};

export const PullRequestTaskBanner = () => {
  const taskId = taskIdFromPath(useLocation().pathname);
  if (!taskId) {
    return null;
  }
  return <PullRequestTaskBannerForTask taskId={taskId} />;
};

export const pullRequestTaskBannerModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    AppRootElementBlueprint.make({
      name: 'pull-request-banner',
      params: {
        element: <PullRequestTaskBanner />,
      },
    }),
  ],
});
